import React, { Component, createRef } from 'react';
import { bool, func, number, string } from 'prop-types';
import styled from 'styled-components';

import { addUserMutation } from 'apps/journey/pages/professional-center/queries/addUserMutation';
import { Input } from 'library/components/form-components';
import ProfCenterModal from 'apps/journey/pages/professional-center/components/prof-center-modal';
import ConfirmationModal from 'apps/journey/components/modals/confirmation-modal';
import { Alert, EmailSent, Plus } from 'library/icons';
import { Header4, Header5 } from 'library/components/text-elements/headings';
import { BodyText } from 'library/components/text-elements/text';
import { isDirty } from 'library/util/form';

const StyledAddIndividualsForm = styled.form`
  display: flex;
  flex-direction: column;
  width: 100%;
  > * {
    margin-top: 2em;
  }
`;

const StyledAddIndividualsFormWrapper = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  height: 100%;
  overflow: auto;
`;

const StyledConfirmWrapper = styled.div`
  max-width: 350px;
  p {
    font-size: 16px;
  }
  h5 {
    font-size: 22px;
  }
`;

const StyledSuccessContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  text-align: center;
  > * {
    margin-top: 1.5em;
  }
`;

class AddIndividuals extends Component {
  constructor(props) {
    super(props);
    this.formRef = createRef();
  }

  state = {
    loading: false,
    addUserForm: {
      send_email: true,
    },
    promptActive: false,
    successStatus: false,
    emailErrors: [],
    individualsCount: 1,
  };

  handleModalToggle = () => {
    if (isDirty(this.formRef?.current)) {
      this.setState({ promptActive: true });
    } else {
      this.props?.toggleActive();
    }
  };

  handleChange = (e) => {
    this.setState({
      addUserForm: {
        ...this.state.addUserForm,
        [e.target.name]: e.target.value,
      },
    });
    if (e.target.name === 'email') {
      this.setState({
        emailErrors: [],
      });
    }
  };

  handleSubmit = async () => {
    let valid = this.formRef.current?.checkValidity();
    if (!valid) {
      this.formRef.current?.reportValidity();
      return;
    }

    this.setState({ loading: true });

    const args = {
      createUserForm: this.state.addUserForm,
      organizationId: this.props.organizationId,
    };

    try {
      const { success, errors } = await addUserMutation(args);

      if (success) {
        this.setState({
          loading: false,
          successStatus: true,
          emailErrors: [],
        });
      } else {
        this.setState({
          emailErrors: errors,
        });
      }
    } catch (e) {
      this.setState({
        emailErrors: e?.data?.errors?.messages || [
          'A user with that email address already exists',
        ],
      });
    }

    this.setState({ loading: false });
  };

  handleNewForm = () => {
    this.setState({
      successStatus: false,
      addUserForm: {
        send_email: true,
      },
    });
  };

  render = () => {
    const { active, toggleActive, headingText, headerDescription } = this.props;
    const {
      promptActive,
      loading,
      successStatus,
      individualsCount,
    } = this.state;

    const individualCountWord = `${individualsCount} individual${
      individualsCount?.length > 1 ? 's' : ''
    }`;

    return (
      <>
        <ProfCenterModal
          active={active}
          loading={loading}
          handleSubmit={successStatus ? toggleActive : this.handleSubmit}
          renderCancelButton={!successStatus}
          submitText={successStatus ? 'Close' : 'Send Invite'}
          toggleActive={successStatus ? toggleActive : this.handleModalToggle}
          modalHeading={successStatus ? '' : headingText}
          modalDetailText={successStatus ? '' : headerDescription}
          icon={!successStatus && <Plus />}
        >
          <StyledAddIndividualsFormWrapper>
            {successStatus ? (
              <StyledSuccessContainer>
                <Header4>Individual invited</Header4>
                <BodyText>
                  An email has been sent to the {individualCountWord} you added.
                  Please ask them to check their email and click on the link to
                  finish creating their account.
                </BodyText>
                <EmailSent width={200} />
              </StyledSuccessContainer>
            ) : (
              <StyledAddIndividualsForm ref={this.formRef}>
                <Input
                  label="First Name*"
                  type="text"
                  name="first_name"
                  required
                  onChange={this.handleChange}
                />
                <Input
                  label="Last Name"
                  type="text"
                  name="last_name"
                  onChange={this.handleChange}
                />
                <Input
                  label="Email*"
                  type="email"
                  name="email"
                  required
                  validationErrors={this.state.emailErrors}
                  onChange={this.handleChange}
                />
                <Input
                  label="Date of Birth*"
                  name="birth_date"
                  type="date"
                  onChange={this.handleChange}
                  required
                />
              </StyledAddIndividualsForm>
            )}
          </StyledAddIndividualsFormWrapper>
        </ProfCenterModal>
        <ConfirmationModal
          cancelText="Cancel"
          confirmText="Confirm"
          toggleActive={() => this.setState({ promptActive: false })}
          handleSubmit={toggleActive}
          active={promptActive}
          customIcon={<Alert width={24} height={24} />}
          customColor="#fec821"
        >
          <StyledConfirmWrapper>
            <Header5>Are you sure you want to exit?</Header5>
            <BodyText>
              This user will not be invited to join and your work will not be
              saved.
            </BodyText>
          </StyledConfirmWrapper>
        </ConfirmationModal>
      </>
    );
  };
}

AddIndividuals.propTypes = {
  active: bool,
  toggleActive: func,
  headingText: string,
  headerDescription: string,
  organizationId: number,
};

export default AddIndividuals;
